import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, X, BarChart3 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { salaryAnalysisAPI, SalaryAnalysisItem } from '../services/api';

function formatDt(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatSalary(rub: number | null | undefined): string {
  if (!rub || rub <= 0) return '—';
  return new Intl.NumberFormat('ru-RU').format(rub) + ' ₽';
}

const SalaryAnalysisPage: React.FC = () => {
  const [items, setItems] = useState<SalaryAnalysisItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SalaryAnalysisItem | null>(null);

  const load = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const data = await salaryAnalysisAPI.list(page, 50);
      if (data.success) {
        setItems(data.items || []);
        setPagination(data.pagination);
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Не удалось загрузить опросы');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 className="h-7 w-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Анализ зарплаты</h1>
          <p className="text-sm text-gray-600">Заполненные опросы пользователей и AI-аналитика рынка ЗП</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Пользователь</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Должность</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Город</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Опыт</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Уровень</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Формат</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Текущая ЗП</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">AI</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {items.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-500">
                      Нет данных
                    </td>
                  </tr>
                )}
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-blue-50 cursor-pointer"
                    onClick={() => setSelected(item)}
                  >
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDt(item.createdAt)}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900 truncate max-w-[180px]">{item.userFullName || '—'}</div>
                      <div className="text-xs text-gray-500 truncate max-w-[180px]">{item.userEmail || `id:${item.userId}`}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.role || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.city || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.experience || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.level || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.format || '—'}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 font-medium whitespace-nowrap">
                      {formatSalary(item.currentSalary)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {item.aiModel ? (
                        <div>
                          <div className="text-gray-700">{item.aiModel}</div>
                          <div>{item.aiTokens ?? '—'} токенов</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Страница {pagination.page} из {pagination.pages} (всего {pagination.total})
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm border rounded disabled:opacity-50"
                  disabled={pagination.page <= 1}
                  onClick={() => load(pagination.page - 1)}
                >
                  Назад
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-sm border rounded disabled:opacity-50"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => load(pagination.page + 1)}
                >
                  Вперёд
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Опрос #{selected.id}</h3>
              <button type="button" onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Дата</div>
                  <div className="font-medium">{formatDt(selected.createdAt)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Пользователь</div>
                  <div className="font-medium">{selected.userFullName || '—'}</div>
                  <div className="text-xs text-gray-500">{selected.userEmail || `id:${selected.userId}`}</div>
                </div>
                <div>
                  <div className="text-gray-500">Должность</div>
                  <div className="font-medium">{selected.role || '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Город</div>
                  <div className="font-medium">{selected.city || '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Опыт</div>
                  <div className="font-medium">{selected.experience || '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Уровень</div>
                  <div className="font-medium">{selected.level || '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Формат</div>
                  <div className="font-medium">{selected.format || '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Текущая ЗП</div>
                  <div className="font-medium">{formatSalary(selected.currentSalary)}</div>
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold mb-2">AI-анализ</h4>
                {selected.aiModel ? (
                  <div className="text-sm space-y-1">
                    <div>
                      <span className="text-gray-500">Модель: </span>
                      <span className="font-medium">{selected.aiModel}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Токены: </span>
                      <span className="font-medium">{selected.aiTokens ?? '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Время вызова: </span>
                      <span className="font-medium">{formatDt(selected.aiCreatedAt)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Полный AI-ответ не сохраняется. Только метаданные вызова (модель, токены) — сматчены с опросом по времени (±5 мин).
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">AI-вызов не зарегистрирован для этого опроса</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryAnalysisPage;
